import React from "react";
import { RCCButton, RCCGrid, RCCIcon, RCCInput, RCCRangeSlider, RCCSwitch, RCCTooltip } from "react-common-components";
import { Control, FieldErrors, FieldValues } from "react-hook-form";
import InputNumber from "src/components/InputNumber";
import { useTranslation } from "src/context/Translations/context";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import cardStyle from "src/components/OrgChart/components/Milestone/styles";
import Title from "src/components/Title";
import SortableHandle from "src/components/Sortable/Handle";
import InputHoursMinutes from "../inputHoursMinutes";
import { IPhases } from "./interface";

export interface IPhase{
	index: number;
    phase: IPhases;
	register: (RegisterOptions?: any) => void;
    control: Control<FieldValues>;
	error: FieldErrors<FieldValues>;
	controlHours: boolean;
	isCopy: boolean;
    isDisabled: boolean;
	handleRemoveRestriction: (index: number) => void;
}

const Phase: React.FC<IPhase> = ({
	index,
	phase,
	register,
	control,
	error,
	controlHours,
	isCopy,
	isDisabled,
	handleRemoveRestriction
}) => {
	const { translate } = useTranslation();

	return(
		<>
			<RCCGrid
				container
				item
				justifyContent="space-between"
				styleInline={cardStyle}
			>
				<Title color="purple" size="md">
					{index + 1}. {translate("Fase")}
					{error.prtba003 && error.prtba003[index]?.message && (
						<span style={{ display: "flex", alignItems: "center", color: "var(--red)", fontSize: "0.8rem", marginLeft: "4px"}}>
							<RCCIcon
								name="infoCircle"
								color="var(--red)"
								style={{ marginRight: "8px"}}
							/>
							{error.prtba003[index].message}
						</span>
					)}
				</Title>
				<div>
					{
						isCopy &&
						<RCCSwitch 
							name={`prtba003[${index}].copyActions`}
							label={translate("Copiar Ações")} 
							register={register} 
							defaultChecked
							disabled={isDisabled}
						/>
					}
				</div>
							
				<RCCGrid
					container
					item
					alignItems="flex-end"
				>
					<input 
						type="text"
						value={phase.pra003Id}
						{...register(`prtba003[${index}].pra003Id`)} 
						style={{ display: "none" }} 
						readOnly
					/>
					<RCCGrid
						container
						item
						justifyContent="center"
						xs={12}
						sm={10}
					>
						<RCCGrid
							item
							xs={12}
						>
							<RCCInput 
								label={translate("Título")}
								register={register}
								name={`prtba003[${index}].pra003Titulo`}
								maxLength={200}
								defaultValue={phase.pra003Titulo}
								error={error.prtba003 && error.prtba003[index]?.pra003Titulo}
								disabled={isDisabled}
								required
							/>
						</RCCGrid>
						<RCCGrid
							item
							xs={12}
						>
							<DateTimePickerRange
								control={control}
								type="datePicker"
								label={translate("Data Estimada")}
								labelSeparator={translate("Até")}
								nameInit = {`prtba003.${index}.pra003Dtiniest`}
								nameEnd = {`prtba003.${index}.pra003Dtfimest`}
								defaultValueInit={phase?.pra003Dtiniest ? phase?.pra003Dtiniest : new Date()}
								defaultValueEnd={phase?.pra003Dtfimest ? phase?.pra003Dtfimest : new Date()}
								error={error.prtba003 && (error.prtba003[index]?.pra003Dtiniest || error.prtba003[index]?.pra003Dtfimest)}
								required
								props={{
									datePicker: {
										disabled: isDisabled
									}
								}}
							/>
						</RCCGrid>
						<RCCGrid
							container
							justifyContent="space-between"
						>
							<RCCGrid
								item
								xs={5}
							>
								<InputNumber 
									name={`prtba003[${index}].pra003Vlrestimado`} 
									control={control}
									label={translate("Valor Estimado Despesa")} 
									defaultValue={phase.pra003Vlrestimado || 0}
									error={error.prtba003 && error.prtba003[index]?.pra003Vlrestimado}
									required
									disabled={isDisabled}	
								/>
							</RCCGrid>
							<RCCGrid
								item
								xs={5}
							>
								<InputNumber 
									name={`prtba003[${index}].pra003Vlrestreceita`} 
									control={control}
									label={translate("Valor Estimado Receita")} 
									defaultValue={phase.pra003Vlrestreceita || 0}
									error={error.prtba003 && error.prtba003[index]?.pra003Vlrestreceita}
									required
									disabled={isDisabled}	
								/>
							</RCCGrid>
						</RCCGrid>
						<RCCGrid
							container
						>
							<RCCRangeSlider
								control={control}
								name={`prtba003[${index}].pra003Peso`} 
								color="purple"
								label={translate("Peso")}
								defaultValue={phase.pra003Peso}
								valueLabelDisplay="on"
								required
								marks={[
									{
										value: 1,
										label: "1"
									},
									{
										value: 100,
										label: "100"
									}
								]}
								min={1}
								max={100}
								disabled={isDisabled}
								styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
							/>
						</RCCGrid>
						<RCCGrid 
							container
						>
							{
								controlHours && 
								<RCCGrid
									container
									item
									xs={12}
									sm={5}
								>
									<InputHoursMinutes 
										name={`prtba003[${index}].hoursminutes`}
										defaultValue={phase.pra003Horasestimadas}
										control={control}
										disabled={isDisabled}
									/>									
								</RCCGrid>
							}
						</RCCGrid>
					</RCCGrid>
									
					<RCCGrid
						container
						item
						justifyContent="flex-end"
						xs={12}
						sm={2}
					>
						<SortableHandle>
							<RCCTooltip
								color="grey"
								text={translate("Ordenar")}
							>
								<RCCButton
									color="transparent"
									icon={{
										name: "arrows",
										color: "var(--grey)"
									}}
									layout="isRounded"
									disabled={isDisabled}
									tabIndex={-1}
								/>
							</RCCTooltip>	
						</SortableHandle>

						<RCCTooltip
							color="red"
							text={translate("Excluir")}
						>
							<RCCButton
								color="transparent"
								icon={{
									name: "trash",
									color: "var(--red)"
								}}
								layout="isRounded"
								onClick={() => handleRemoveRestriction(index)}
								disabled={isDisabled}
								tabIndex={-1}
							/>
						</RCCTooltip>
					</RCCGrid>
				</RCCGrid>
			</RCCGrid>
		</>
	);
};

export default Phase;
